import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { apiGet, apiPost } from "../api/http";
import { useToast } from "./ToastContext";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewUserLogin, setIsNewUserLogin] = useState(false);
  const welcomeToastRef = useRef(false);
  const userRef = useRef(null);
  userRef.current = user;
  const { showSuccess } = useToast();

  const fetchUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    setLoading((currLoading) => {
      if (!userRef.current) return true;
      return currLoading;
    });

    try {
      const { data } = await apiGet("/api/auth/me");
      setUser(data.user);
      
      // Show welcome toast if this was a fresh login, BUT skip if inside an iframe (like the widget)
      const isIframe = window.self !== window.top;

      if (token && !userRef.current && data.user && !isIframe && welcomeToastRef.current) {
        if (isNewUserLogin) {
          showSuccess(`Great to have you, ${data.user.name || "friend"}!`, 5000);
        } else {
          showSuccess(`Welcome back, ${data.user.name || "friend"}!`, 5000);
        }
        welcomeToastRef.current = false;
        setIsNewUserLogin(false);
      }
    } catch (err) {
      // Token invalid or expired
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, isNewUserLogin, showSuccess]);

  const csrfFetchedRef = useRef(false);

  useEffect(() => {
    const isIframe = window.self !== window.top;
    if (isIframe) {
      setLoading(false);
      return;
    }

    fetchUser();

    // Fetch CSRF token only once on mount (not on every token refresh)
    if (!csrfFetchedRef.current) {
      csrfFetchedRef.current = true;
      apiGet("/api/auth/csrf-token")
        .then(({ data }) => {
          if (data?.csrfToken) {
            localStorage.setItem("csrf_token", data.csrfToken);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch initial CSRF token", err);
        });
    }
  }, [token]);

  const login = useCallback((newToken, expiresAt = null, refreshToken = null, isNewUser = false) => {
    localStorage.setItem("token", newToken);
    if (expiresAt) {
      localStorage.setItem("tokenExpiresAt", expiresAt);
    }
    setIsNewUserLogin(isNewUser);
    welcomeToastRef.current = true;

    // Trigger loading and clear stale profile for manual logins
    setUser((currentUser) => {
      if (!currentUser) {
        setLoading(true);
        return null;
      }
      return currentUser;
    });

    setToken(newToken);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const { data } = await apiGet("/api/auth/profile");
      setUser(data.user);
      return data.user;
    } catch (err) {
      console.warn("Failed to refresh user profile", err);
      return null;
    }
  }, [token]);

  const acceptTerms = useCallback(async (name) => {
    if (!token) return { success: false, error: "Not authenticated" };
    try {
      const { data } = await apiPost("/api/auth/accept-terms", { name, acceptedTerms: true });
      setUser(data.user);
      return { success: true };
    } catch (err) {
      console.error("Failed to accept terms", err);
      const errMsg = err?.response?.data?.error || "Failed to update profile";
      return { success: false, error: errMsg };
    }
  }, [token]);

  const logout = useCallback(async () => {
    try {
      await apiPost("/api/auth/logout");
    } catch (err) {
      console.warn("Logout request failed", err);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("tokenExpiresAt");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("refreshTokenId");
      setToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const expiresAt = localStorage.getItem("tokenExpiresAt");
    if (!token || !expiresAt) return;
 
    const expiryTime = Number(expiresAt) * 1000;
    const now = Date.now();
    const delay = expiryTime - now - 60000; // 1 minute before expiry
 
    const doRefresh = async () => {
      try {
        const { data } = await apiPost("/api/auth/refresh");
        // Only update token state if a new, different token was issued
        if (data.token && data.token !== token) {
          login(data.token, data.expiresAt, null, false);
        }
      } catch (err) {
        logout();
      }
    };

    if (delay <= 0) {
      // Refresh immediately if already close to expiry or expired
      doRefresh();
      return;
    }
 
    const timer = setTimeout(doRefresh, delay);
    return () => clearTimeout(timer);
  }, [token, login, logout]);

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ token, user, isAuthenticated, login, logout, refreshUser, acceptTerms, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
